import React, { useCallback } from 'react'

import { Snackbar, Alert } from '@mui/material'

import { useAppDispatch, useAppSelector } from '../../../../hooks/storeHooks'
import { displaySnackBar } from '../../actions/displaySnackBar'
import { SnackBarMessage } from '../../types/SnackBarMessage'
import './GRSnackBar.scss'

const GRSnackBar: React.FC = () => {
  const dispatch = useAppDispatch()

  const snackBarMessage: SnackBarMessage | undefined = useAppSelector((state) => {
    return state.snackBarMessage
  })

  const handleClose = useCallback(() => {
    dispatch(displaySnackBar({ message: snackBarMessage.message, severity: snackBarMessage.severity, open: false }))
  }, [dispatch, snackBarMessage])

  return (
    <div className="GRSnackBar">
      {snackBarMessage ? (
        <Snackbar
          open={snackBarMessage.open}
          autoHideDuration={snackBarMessage.displayTimeMilliseconds ? snackBarMessage.displayTimeMilliseconds : 3000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleClose}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={snackBarMessage.severity}
            onClose={handleClose}
            sx={{
              // Cap the alert height so very long messages (e.g. long selected game lists) never
              // extend past the viewport top. 48px = the Snackbar's 24px bottom offset + an equal
              // 24px margin left at the top.
              maxHeight: 'calc(100vh - 48px)',
              '& .MuiAlert-message': {
                // The Alert root is a flex row, and maxHeight on a flex container does not shrink
                // its children on the cross axis, so the message region is capped directly:
                // 60px = the 48px above + the Alert's 12px of vertical padding.
                maxHeight: 'calc(100vh - 60px)',
                overflowY: 'auto',
              },
              // Selected-games snackbar: keep "Games selected:" and the close button fixed,
              // and put the scrollbar on the list at the alert's far right edge (GR-2988).
              // display:contents lets the header and list participate in this grid so the list
              // can span under the close button instead of scrolling in the inset message column.
              '&:has(.SelectedGamesSnackbarMessage)': {
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                gridTemplateRows: 'auto minmax(0, 1fr)',
                alignItems: 'start',
                overflow: 'hidden',
                '& .MuiAlert-icon': {
                  gridColumn: 1,
                  gridRow: 1,
                },
                '& .MuiAlert-message': {
                  display: 'contents',
                  maxHeight: 'none',
                  overflow: 'visible',
                },
                '& .MuiAlert-action': {
                  gridColumn: 3,
                  gridRow: 1,
                  marginLeft: 0,
                },
                '& .SelectedGamesSnackbarMessage': {
                  display: 'contents',
                },
                '& .SelectedGamesSnackbarMessage__header': {
                  gridColumn: 2,
                  gridRow: 1,
                  alignSelf: 'center',
                  paddingTop: '8px',
                },
                '& .SelectedGamesSnackbarMessage__list': {
                  gridColumn: '2 / -1',
                  gridRow: 2,
                  // The grid's alignItems:'start' would let the list size to its full content,
                  // which defeats the minmax(0, 1fr) row cap and the overflowY below; stretching
                  // the list to its row keeps the scrollbar active (GR-2988).
                  alignSelf: 'stretch',
                  minHeight: 0,
                  minWidth: 0,
                  overflowY: 'auto',
                  m: 0,
                  pl: 2.5,
                  pb: 1,
                },
              },
            }}
          >
            {snackBarMessage.message}
          </Alert>
        </Snackbar>
      ) : null}
    </div>
  )
}

export default GRSnackBar
